import React, { useEffect, useState, useContext } from 'react';
import { Newspaper, Send, Edit3, LayoutDashboard, List, Settings2 } from 'lucide-react';
import { YearContext } from '../components/Layout/Layout';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/Cards/StatCard';
import FilterBar from '../components/Common/FilterBar';
import ImportExport from '../components/Common/ImportExport';
import DataTable, { StatusBadge } from '../components/Common/DataTable';
import { BarChartComponent, LineChartComponent, PieChartComponent } from '../components/Charts/ChartComponents';
import { newsletterAPI } from '../services/api';
import { PageLoader } from '../components/Common/LoadingSpinner';
import EmailNotificationSetup       from '../components/Common/EmailNotificationSetup';
import TelegramNotificationSetup    from '../components/Common/TelegramNotificationSetup';
import NotificationThresholdCard    from '../components/Common/NotificationThresholdCard';

const TABS = [
  { id: 'dashboard',   label: 'Dashboard',   icon: LayoutDashboard },
  { id: 'newsletter',  label: 'Newsletter',  icon: List            },
  { id: 'setup',       label: 'Setup',       icon: Settings2       },
];

const columns = [
  { key: 'teacher',       label: 'Teacher',   sortable: true },
  { key: 'grade_section', label: 'Grade',     sortable: true },
  { key: 'title',         label: 'Title' },
  { key: 'publish_date',  label: 'Published', sortable: true, render: v => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
  { key: 'status',        label: 'Status',    render: v => <StatusBadge status={v} /> },
  { key: 'week_number',   label: 'Week',      sortable: true },
];

export default function Newsletter() {
  const { yearId, refreshKey } = useContext(YearContext);
  const { isAdmin, isSuperAdmin } = useAuth();
  const canSetup = isAdmin || isSuperAdmin;

  const [tab,     setTab]     = useState('dashboard');
  const [filters, setFilters] = useState({ month: '', week: '', startDate: '', endDate: '' });
  const [stats,   setStats]   = useState(null);
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);

  const params = { academicYearId: yearId, ...filters };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      newsletterAPI.getStats(params),
      newsletterAPI.getList({ ...params, limit: 200 }),
    ]).then(([s, l]) => {
      setStats(s.data);
      setList(l.data.data || []);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, [yearId, refreshKey, JSON.stringify(filters)]); // eslint-disable-line

  const teacherData = (stats?.byTeacher    || []).map(t => ({ name: t.teacher.split(' ')[0], contributions: t.contributions }));
  const gradeData   = (stats?.byGrade      || []).map(g => ({ name: g.grade, value: g.newsletters }));
  const monthlyData = (stats?.monthlyTrend || []).map(m => ({ month: `M${m.month}`, count: m.total }));

  if (loading && tab !== 'setup') return <PageLoader />;

  const visibleTabs = TABS.filter(t => t.id !== 'setup' || canSetup);

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Page header with tab navigation ── */}
      <div className="space-y-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-display font-bold text-slate-800">Newsletter</h2>
            <p className="text-slate-500 text-sm mt-0.5">Track newsletter contributions by teachers and grades</p>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm shrink-0">
            {visibleTabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  tab === t.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Second row — Import/Export (newsletter tab only) */}
        {tab === 'newsletter' && (
          <div className="flex justify-end">
            <ImportExport
              compact
              module="Newsletter"
              onImport={fd => newsletterAPI.import(fd, { academicYearId: yearId })}
              onExport={() => newsletterAPI.export(params)}
              onSampleTemplate={newsletterAPI.sampleTemplate}
            />
          </div>
        )}
      </div>

      {/* ── Dashboard ───────────────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard title="Total Newsletters"  value={stats?.overall?.total     || 0} icon={Newspaper} color="blue"   />
            <StatCard title="Published"          value={stats?.overall?.published || 0} icon={Send}      color="green"  />
            <StatCard title="Drafts"             value={stats?.overall?.drafts    || 0} icon={Edit3}     color="yellow" />
          </div>

          <FilterBar filters={filters} onChange={setFilters} />

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BarChartComponent
              title="Teacher-wise Newsletter Contributions"
              data={teacherData} xKey="name"
              bars={[{ key: 'contributions', label: 'Count', color: '#0ea5e9' }]}
            />
            <PieChartComponent
              title="Grade-wise Newsletter Distribution"
              data={gradeData} donut
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LineChartComponent
              title="Monthly Newsletter Count"
              data={monthlyData} xKey="month"
              lines={[{ key: 'count', label: 'Newsletters', color: '#06b6d4' }]}
            />
            <BarChartComponent
              title="Monthly Comparison"
              data={monthlyData} xKey="month"
              bars={[{ key: 'count', label: 'Count', color: '#84cc16' }]}
            />
          </div>
        </>
      )}

      {/* ── Newsletter list ──────────────────────────────────────────── */}
      {tab === 'newsletter' && (
        <>
          <FilterBar filters={filters} onChange={setFilters} />
          <DataTable
            columns={columns}
            data={list}
            loading={loading}
            emptyText="No newsletter data found"
          />
        </>
      )}

      {/* ── Setup ───────────────────────────────────────────────────── */}
      {tab === 'setup' && canSetup && (
        <div className="space-y-5">
          <NotificationThresholdCard
            module="newsletter"
            yearId={yearId}
            title="Newsletter Reminder Thresholds"
            thresholds={[
              {
                key: 'publish_reminder_days',
                label: 'Reminder days before scheduled publish date',
                description: 'Send a reminder notification this many days before a newsletter is due to be published.',
                defaultValue: 3,
                min: 1,
                max: 30,
                suffix: 'days before publish date',
              },
            ]}
          />
          <EmailNotificationSetup    module="newsletter" moduleLabel="Newsletter" yearId={yearId} />
          <TelegramNotificationSetup module="newsletter" moduleLabel="Newsletter" yearId={yearId} />
        </div>
      )}
    </div>
  );
}
